import { useContext, useEffect, useState } from "react";
import UserContext from "../../../contexts/UserContext";
import SupplierContext from "../../../contexts/SupplierContext";
import VendorContext from "../../../contexts/VendorContext";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IOnAddItemToCartDTO from "../../../models/DTO/IOnAddItemToCartDTO";
import IOnUpdateCartItemDTO from "../../../models/DTO/IOnUpdateCartItemDTO";
import { useCart } from "../../../contexts/CartContext";
import { EcommerceCartFnItem } from "../EcommerceCartFnItem/EcommerceCartFnItem";
import ICart_Item from "../../../models/ICart_Item";
import IUpdateCartItemDTO from "../../../models/DTO/IUpdateCartItemDTO";
import styled from "styled-components";
import ActivityIndicator from "../../shared/ActivityIndicator/ActivityIndicator";
import IDeleteCartItemDTO from "../../../models/DTO/IDeleteCartItemDTO";
import IOnDeleteCartItemDTO from "../../../models/DTO/IOnDeleteCartItemDTO";
import { Socket } from "socket.io-client";
import IUnit_Of_Measurement_Set from "../../../models/IUnit_Of_Measurement_Set";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import UiContext from "../../../contexts/UiContext";

type Props = {
    socket: Socket;
};

export const EcommerceCart = (props: Props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const vendorModel = useContext(VendorContext);
    const uiContext = useContext(UiContext);
    const { cart, refreshCart, isLoading } = useCart();

    const [cartUpdating, setCartUpdating] = useState(false);

    const priceGroup = vendorModel.Price_Group;

    useEffect(() => {
        if (cart?.Cart_Items) {
            // we only want the cart items without the kit child items.

            let count = 0;

            if (cart?.Cart_Items) {
                for (const item of cart.Cart_Items) {
                    if (!item.parent_cart_item_id) {
                        count++;
                    }
                }
            }

            uiContext.setCartItemCount(count);
        }
    }, [cart]);

    const onAddItem = (data: IOnAddItemToCartDTO) => {
        refreshCart();
        setCartUpdating(false);
    };

    const onUpdateItem = (data: IOnUpdateCartItemDTO) => {
        refreshCart();
        setCartUpdating(false);
    };

    const onDeleteItem = (data: IOnDeleteCartItemDTO) => {
        refreshCart();
        setCartUpdating(false);
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.CartOnAddItem, fn: onAddItem },
        { endpoint: EndpointsEnum.CartOnUpdateItem, fn: onUpdateItem },
        { endpoint: EndpointsEnum.CartOnDeleteItem, fn: onDeleteItem },
    ];

    useEffect(() => {
        for (const socket of sockets) {
            props.socket.on(socket.endpoint, socket.fn);
        }

        refreshCart();

        return () => {
            for (const socket of sockets) {
                props.socket.removeListener(socket.endpoint, socket.fn);
            }
        };
    }, []);

    const updateQty = (cartItem: ICart_Item, qty: number, set?: IUnit_Of_Measurement_Set) => {
        const sameQty = cartItem.qty === qty;
        const sameUom = !set || set.id === cartItem.uom_set_id;
        if (sameQty && sameUom) {
            return;
        }

        const payload: IUpdateCartItemDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            qty,
            cartItemGuid: cartItem.guid,
        };

        if (!userModel.isSupplierUser) {
            payload.vendorGuid = vendorModel.guid;
        }

        if (set) {
            payload.uomSetId = set.id;
        }

        props.socket.emit(EndpointsEnum.CartUpdateItem, payload);

        setCartUpdating(true);
    };

    const deleteItem = (cartItem: ICart_Item) => {
        const payload: IDeleteCartItemDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            cartItemGuid: cartItem.guid,
        };

        if (!userModel.isSupplierUser) {
            payload.vendorGuid = vendorModel.guid;
        }

        props.socket.emit(EndpointsEnum.CartDeleteItem, payload);

        setCartUpdating(true);
    };

    return (
        <EcommerceCartDiv>
            {cartUpdating ? (
                <EcommerceCartBusy>
                    <ActivityIndicator active={cartUpdating} activeClassName={""} />
                </EcommerceCartBusy>
            ) : null}
            {cart?.Cart_Items?.filter((cartItem) => !cartItem.main_kit_cart_item_id).map((cartItem, index) => {
                let kitBuilderItems: ICart_Item[] | undefined = [];

                if (cartItem.is_kit_builder) {
                    kitBuilderItems = cart?.Cart_Items?.filter((kitCartItem) => {
                        return kitCartItem.main_kit_cart_item_id === cartItem.id;
                    });
                }

                return (
                    <EcommerceCartFnItem
                        key={cartItem.guid}
                        cartItem={cartItem}
                        priceGroup={priceGroup}
                        updateQty={(qty, set?: IUnit_Of_Measurement_Set) => {
                            updateQty(cartItem, qty, set);
                        }}
                        deleteItem={deleteItem}
                        kitBuilderItems={kitBuilderItems}
                    />
                );
            })}
        </EcommerceCartDiv>
    );
};

const EcommerceCartDiv = styled.div`
    position: relative;
    border-top: 1px solid var(--main-text-color);
    width: 100%;
    height: 100%;
    overflow-y: scroll;
`;

const EcommerceCartBusy = styled.div`
    position: absolute;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.1);
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--highlight-color);
`;
